import { HttpRequest, HttpHandlerFn, HttpEvent } from '@angular/common/http';
import { Injectable, inject, effect } from '@angular/core';
import { Subject, Observable, take, switchMap, EMPTY } from 'rxjs';
import { AuthDataService } from 'src/data-store/auth-data.service';
import { ELoginDeviceType, IRefreshResult } from 'src/models/auth.model';
import { ELoadingEntity } from 'src/models/generic-store.model';
import { AuthLinksService } from './auth-links.service';
import { DeviceService } from './device.service';
import { InAppBrowserAuthService } from './in-app-browser-auth.service';
import { LoggerService } from './logger.service';

@Injectable({ providedIn: 'root' })
export class TokenRefreshService {
  private authDataService = inject(AuthDataService);
  private deviceService = inject(DeviceService);
  private authLinksService = inject(AuthLinksService);
  private inAppBrowserAuthService = inject(InAppBrowserAuthService);
  private loggerService = inject(LoggerService);

  private isRefreshing = false;
  private tokenBeforeRefresh: string | null = null;
  private refreshComplete$ = new Subject<IRefreshResult>();

  public constructor() {
    effect(() => {
      const tokenData = this.authDataService.tokenData();
      const authError = this.authDataService.authGetError(ELoadingEntity.AUTH_TOKEN_DATA);
      const isLoading = this.authDataService.authIsLoading(ELoadingEntity.AUTH_TOKEN_DATA);

      if (!this.isRefreshing || isLoading) {
        return;
      }

      if (tokenData?.access_token && this.tokenBeforeRefresh
          && tokenData.access_token !== this.tokenBeforeRefresh) {
        this.loggerService.logInfo('Received HTTP 401 error(s), will refresh token and trigger failed requests again.');
        this.settle({ success: true, tokenData });
        return;
      }

      if (authError?.error) {
        this.settle({ success: false, error: authError });
      }
    });
  }

  public handle401(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
    const refreshToken = this.authDataService.tokenData()?.refresh_token;

    if (!refreshToken) {
      this.authDataService.resetAuthState();
      return this.redirectToLogin();
    }

    const waitFor$ = this.refreshComplete$; // capture before settle() swaps it

    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.tokenBeforeRefresh = this.authDataService.accessToken();
      const refreshType = this.deviceService.isNativePlatform ? ELoginDeviceType.MOBILE : ELoginDeviceType.WEB;
      this.authDataService.refreshToken(refreshType, refreshToken);
    }

    return waitFor$.pipe(
      take(1),
      switchMap(result => {
        if (result.success && result.tokenData) {
          return next(this.addTokenHeader(req));
        }
        this.authDataService.resetAuthState();
        return this.redirectToLogin();
      })
    );
  }

  private settle(result: IRefreshResult): void {
    this.refreshComplete$.next(result);
    this.refreshComplete$.complete();
    this.isRefreshing = false;
    this.tokenBeforeRefresh = null;
    this.refreshComplete$ = new Subject<IRefreshResult>();
  }

  private addTokenHeader(req: HttpRequest<unknown>): HttpRequest<unknown> {
    const token = this.authDataService.accessToken();
    return token
      ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
      : req;
  }

  private redirectToLogin(): Observable<HttpEvent<unknown>> {
    if (this.deviceService.isNativePlatform) {
      this.inAppBrowserAuthService.openInAppBrowser();
    } else {
      window.location.href = this.authLinksService.bcasClientLoginUrl;
    }
    return EMPTY;
  }
}